'use client';

import type { PropsWithChildren } from 'react';
import { useParams, useSelectedLayoutSegments } from 'next/navigation';
import type { ProjectTreeItemType } from '@frameio/project-tree';
import { createActivationManager } from '@frameio/vapor-migration';

const { ActivationManager, useActivation, useActivationIsPending } =
  createActivationManager<string>();

export type ProjectNavItemType = ProjectTreeItemType | 'all-shares' | 'share';

export const useProjectNavigationActivationPending = useActivationIsPending;
export const useProjectNavigationActivation = () => {
  const { activate, state } = useActivation();

  return {
    // this wrapper helps tighten up the types so only valid items can be activated
    activate: (id: string, type: ProjectNavItemType, fn: () => void) =>
      activate(`${type}:${id}`, fn),
    state,
  };
};

/**
 * App directory activation manager
 */

export function ProjectNavigationActivationManager({
  children,
  rootFolderId,
}: PropsWithChildren<{
  rootFolderId: string;
}>) {
  const id = useProjectNavId({ rootFolderId });

  return (
    <ActivationManager activeEntity={id ?? ''} mode="transition">
      {children}
    </ActivationManager>
  );
}

function useProjectNavId({
  rootFolderId,
}: {
  rootFolderId: string;
}): string | null {
  const segments = useSelectedLayoutSegments();
  const params = useParams();

  if (segments.includes('c2c-connections')) {
    return 'c2c-connections:c2c-connections';
  }

  if (segments.includes('upload-links')) {
    return 'upload-links:upload-links';
  }

  if (segments.includes('collection')) {
    return `collection:${String(params.collectionId)}`;
  }

  if (segments.includes('shares')) {
    return 'all-shares:all-shares';
  }

  if (segments.includes('collections')) {
    return 'all-collections:all-collections';
  }

  if (segments.includes('share-builder')) {
    return `share:${String(params.shareId)}`;
  }

  if (segments.includes('recently-deleted')) {
    return 'trash:trash';
  }

  if (segments.includes('(folders)')) {
    if (!params.folderAssetId) {
      return `root-folder:${rootFolderId}`;
    }

    return `folder:${String(params.folderAssetId)}`;
  }

  return null;
}
